import { DZ_COLOR_NEUTRAL_300 } from '@blk/daisy/core/styles';
import { CURRENCY_SYMBOLS } from '@ddt-web/common';
import { NumberWithUnitsFormatter } from '@ddt-web/data-service';
import { ObjectUtil } from '@ddt-web/generic-utils';
import * as Highcharts from 'highcharts';
import more from 'highcharts/highcharts-more';

import { ChartFactory } from '../factories';
import { ProgressBarDataConfig } from '../models/progress-bar.interface';
import { ChartConfigHelper } from './config.helper';
import { DefaultChartConfig } from './default-chart.config';
import { ChartType } from './enums/chart-type.enum';

more(Highcharts);

const DEFAULT_CONFIG: any = {
	legend: {
		enabled: true,
		symbolRadius: 0,
		reversed: true,
		itemStyle: { fontSize: '8px', fontWeight: 'normal' },
		itemMarginBottom: 4,
		x: -7,
		y: 8
	}
};

const INCOME_GAP_WIDTH_PROPORTION: number = 0.04; // The preceeding income bar gap when goal isn't achieved will be 4% of the progress bar width.

export class ProgressBarConfig extends DefaultChartConfig {
	private seriesColors: Array<string> = [];

	private displayCurrency: string = CURRENCY_SYMBOLS.USD;
	private multiplier: number = 10000;

	constructor(
		protected type: ChartType,
		protected name: string,
		protected container: HTMLElement,
		protected data: ProgressBarDataConfig,
		protected dataIndex?: number,
		protected colors?: Array<string>,
		protected height?: number,
		protected width?: number,
		protected title?: string,
		protected hideTitle?: boolean,
		protected x?: string,
		protected y?: string,
		protected customAttributes?: any,
		protected tooltipType?: string
	) {
		super(type, container, colors, height, width, title, hideTitle);
		this.initialize();
	}

	public render(): void {
		Highcharts.chart(this.configuration);
	}

	public getConfigurationForPDF(): void {
		return this.configuration;
	}

	private initialize(): void {
		this.displayCurrency = this.customAttributes?.displayCurrency;
		this.multiplier = this.customAttributes?.growthMultiplier || this.multiplier;
		this.configuration = ObjectUtil.extend(this.configuration, DEFAULT_CONFIG);
		this.seriesColors = this.configuration.colors || ChartFactory.getPortfolioColors();
		this.updateDefaultConfiguration();
		const { growthMultiplier, displayCurrency, tooltipPrefixes, multiSeriesConfig, ...validHighChartAttributes } = this.customAttributes;
		this.configuration = ChartConfigHelper.applyCustomAttributesToConfiguration(validHighChartAttributes, this.configuration);
	}

	private updateDefaultConfiguration(): void {
		const displayCurrency = this.displayCurrency;
		const isAnnuityVariant = this.customAttributes?.isAnnuityVariant;
		const formatter = new NumberWithUnitsFormatter('0', '-', true);
		const max = this.data['max'];
		const isGoalAchieved = this.data['goalAchieved'];
		this.configuration = ObjectUtil.extend(this.configuration, {
			colors: this.seriesColors,
			chart: {
				type: 'bar',
				style: {
					fontFamily: 'FortBook, Arial, sans-serif'
				}
			},
			title: {
				text: this.customAttributes.customData?.header,
				align: 'center',
				margin: 0
			},
			subtitle: {
				text: this.customAttributes.customData?.subHeader,
				verticalAlign: 'bottom'
			},
			credits: { enabled: false },
			legend: {
				enabled: true,
				useHTML: true,
				itemStyle: {
					fontSize: '8px',
					fontWeight: 'normal'
				},
				itemDistance: isAnnuityVariant ? 5 : 20,
				itemMarginBottom: 4,
				symbolRadius: 0,
				reversed: true,
				width: '100%',
				symbolPadding: 2,
				y: 8
			},
			tooltip: { enabled: false },
			plotOptions: {
				bar: {
					stacking: 'normal',
					borderWidth: 0
				}
			},
			xAxis: {
				visible: false
			},
			yAxis: {
				visible: true,
				min: 0,
				title: {
					text: null
				},
				gridLineWidth: 0,
				tickPositions: this.updateTickPositions(max),
				labels: {
					y: 0,
					style: {
						fontSize: '9px',
						color: DZ_COLOR_NEUTRAL_300
					},
					formatter: function (): string {
						const label = formatter.format(Number(this.value));
						if ((!isGoalAchieved && this.value > max) || (isGoalAchieved && this.value > max + max * INCOME_GAP_WIDTH_PROPORTION)) {
							return '';
						}
						return displayCurrency + label;
					}
				}
			},
			series: this.data.series
		});
	}

	private updateTickPositions(max: number): Array<number> {
		const ticks = [];
		for (let i = 0; i <= max; i += max / 10) {
			ticks.push(i);
		}
		if (!this.data['goalAchieved']) {
			ticks.push(max + max * INCOME_GAP_WIDTH_PROPORTION);
		}
		return ticks;
	}
}
